import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SEQUENCE_RESETS,
  SETTINGS_ROLES,
  formatSequence,
  patternProblem,
  type CatalogueKey,
  type SequenceReset,
  type SequenceRow,
} from '@mahallu/shared';
import { ApiError, loadErrorKey, sequences, useMe, useSequences } from '../api';
import { Badge, PageHeader } from '../components/Parts';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

function NextValue({ row, scopeKey, nextValue, canEdit }: { row: SequenceRow; scopeKey: string; nextValue: number; canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const raise = useMutation({
    mutationFn: () => sequences.raiseNext(row.code, Number(value), scopeKey || undefined),
    onSuccess: () => {
      setValue('');
      void qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
  const wanted = Number(value);
  const valid = Number.isInteger(wanted) && wanted > nextValue;
  return (
    <tr>
      {row.scope === 'WARD' ? <td>{scopeKey}</td> : null}
      <td className="num">
        <b className="seq-number">{formatSequence(row.pattern, nextValue, { at: new Date(), ward: scopeKey || undefined })}</b>
      </td>
      <td>
        {canEdit ? (
          <form
            className="seq-raise"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid) raise.mutate();
            }}
          >
            <input
              type="number"
              inputMode="numeric"
              min={nextValue + 1}
              value={value}
              placeholder={String(nextValue + 1)}
              aria-label={`${t('seq.raise')} ${scopeKey}`}
              onChange={(e) => setValue(e.target.value)}
            />
            <button type="submit" className="btn sm" disabled={!valid || raise.isPending}>
              {t('seq.set')}
            </button>
          </form>
        ) : null}
        {raise.isError ? <p className="form-error">{errorText(raise.error)}</p> : null}
      </td>
    </tr>
  );
}

function SequenceCard({ row, canEdit }: { row: SequenceRow; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [pattern, setPattern] = useState(row.pattern);
  const [reset, setReset] = useState<SequenceReset>(row.reset);
  const [saved, setSaved] = useState(false);
  // A saved change (new version) refreshes the fields to the stored values; "Saved." stays visible.
  useEffect(() => {
    setPattern(row.pattern);
    setReset(row.reset);
  }, [row.version, row.pattern, row.reset]);
  const problem = patternProblem(pattern, row.scope, reset);
  const changed = pattern !== row.pattern || reset !== row.reset;
  const sample = row.next[0];
  const save = useMutation({
    mutationFn: () => sequences.update(row.code, { pattern, reset, version: row.version }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
  const name = i18n.language === 'ml' && row.nameMl ? row.nameMl : row.nameEn;

  return (
    <section className="card seq-card" aria-labelledby={`seq-${row.code}`}>
      <div className="card-h">
        <h2 className="card-t" id={`seq-${row.code}`}>
          {name}
        </h2>
        {row.scope === 'WARD' ? <Badge tone="teal">{t('seq.scope.WARD')}</Badge> : null}
      </div>

      <div className="seq-form">
        <label className="field">
          {t('seq.pattern')}
          <input
            className="mono"
            value={pattern}
            maxLength={40}
            disabled={!canEdit}
            spellCheck={false}
            onChange={(e) => {
              setPattern(e.target.value.toUpperCase().replace(/\{([a-z0-9]+)\}/gi, (m) => m.toUpperCase()));
              setSaved(false);
            }}
          />
        </label>
        <label className="field">
          {t('seq.reset')}
          <select
            value={reset}
            disabled={!canEdit}
            onChange={(e) => {
              setReset(e.target.value as SequenceReset);
              setSaved(false);
            }}
          >
            {SEQUENCE_RESETS.map((r) => (
              <option key={r} value={r}>
                {t(`seq.reset.${r}` as CatalogueKey)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="note">{t('seq.tokens')}</p>
      {problem ? (
        <p className="form-error" role="alert">
          {problem}
        </p>
      ) : (
        <p className="seq-preview">
          {t('seq.preview')}{' '}
          <span className="num">
            {sample ? formatSequence(pattern, sample.nextValue, { at: new Date(), ward: sample.scopeKey || undefined }) : '—'}
          </span>
        </p>
      )}

      {canEdit ? (
        <div className="actions">
          <button type="button" className="btn primary sm" disabled={!changed || Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
            {t('seq.save')}
          </button>
          {saved && !changed ? <span className="note c-ok">{t('seq.saved')}</span> : null}
          <span className="note">{t('seq.resetHint')}</span>
        </div>
      ) : null}
      {save.isError ? <p className="form-error">{errorText(save.error)}</p> : null}

      <div className="tbl-scroll seq-next">
        <table>
          <thead>
            <tr>
              {row.scope === 'WARD' ? <th>{t('seq.ward')}</th> : null}
              <th>{t('seq.next')}</th>
              <th>{canEdit ? t('seq.raise') : ''}</th>
            </tr>
          </thead>
          <tbody>
            {row.next.map((n) => (
              <NextValue key={n.scopeKey || 'all'} row={row} scopeKey={n.scopeKey} nextValue={n.nextValue} canEdit={canEdit} />
            ))}
          </tbody>
        </table>
      </div>
      {canEdit ? <p className="note">{t('seq.raiseHint')}</p> : null}
    </section>
  );
}

/** Settings › Numbering. Every number the product issues comes from these series (ADR-0017). */
export function SequenceMaster() {
  const { t } = useTranslation();
  const me = useMe();
  const list = useSequences();
  const canEdit = Boolean(me.data?.role && SETTINGS_ROLES.includes(me.data.role));

  return (
    <div className="content">
      <PageHeader icon="hash" title={t('seq.title')} desc={t('seq.desc')} />
      {!canEdit && me.data ? <p className="note">{t('seq.readOnly')}</p> : null}
      {list.isLoading ? <p className="tbl-msg">{t('table.loading')}</p> : null}
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="seq-grid">
        {list.data?.map((row) => (
          <SequenceCard key={row.code} row={row} canEdit={canEdit} />
        ))}
      </div>
    </div>
  );
}
